import React, { useMemo, useRef, useState } from 'react';
import { FiBookOpen, FiFileText, FiLock, FiMic, FiVolume2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useDocuments } from '../hooks/useDocuments';
import { useEntitlements } from '../entitlements/useEntitlements';
import { useAudioUsage } from '../entitlements/useAudioUsage';
import { useAudioPlayer } from '../audio/AudioPlayerContext';
import { generateAudioStream, textToSpeech } from '../api/audioApi';
import { DepthLevel, GenerateAudioPayload, LengthLevel, TtsVoice } from '../types';
import {
  DEFAULT_PODCAST_SPEAKERS,
  evaluateGate,
  FREE_PODCAST_DEPTHS,
  FREE_TTS_CHAR_CAP,
  GenFormat,
  GenOptions,
  MAX_TTS_CHARS,
  TIER_RANK,
} from '../entitlements/gating';
import { AUDIO_CREATED_EVENT } from '../hooks/useLibrary';
import DocumentPickerModal from '../components/DocumentPickerModal';
import UpgradeModal from '../components/UpgradeModal';
import AdSlot from '../components/AdSlot';
import '../pages/pages.css';
import './CreatePage.css';

const PODCAST_DEPTHS: { value: DepthLevel; label: string; hint: string }[] = [
  { value: 'brief', label: 'Brief', hint: '~5–7 min' },
  { value: 'moderate', label: 'Standard', hint: '~15–20 min' },
  { value: 'comprehensive', label: 'Deep', hint: '~30–35 min' },
  { value: 'exhaustive', label: 'Exhaustive', hint: '~45–60 min' },
];

const AUDIOBOOK_LENGTHS: { value: LengthLevel; label: string; hint: string }[] = [
  { value: 'brief', label: 'Brief', hint: '~7–15 min' },
  { value: 'moderate', label: 'Standard', hint: '~30–50 min' },
  { value: 'comprehensive', label: 'Deep', hint: '~35–60 min' },
  { value: 'full', label: 'Full', hint: '~50–90 min' },
];

const TONES = ['Default', 'Conversational', 'Energetic', 'Calm', 'Academic'];
const SPEAKER_OPTIONS = [2, 3, 4];

function formatReset(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function CreatePage() {
  const navigate = useNavigate();
  const { documents, isReady } = useDocuments();
  const { tier } = useEntitlements();
  const { usage, refresh: refreshUsage } = useAudioUsage();
  const player = useAudioPlayer();

  const [format, setFormat] = useState<GenFormat>('podcast');

  // Shared doc-based options
  const [selectedDocs, setSelectedDocs] = useState<number[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [depth, setDepth] = useState<DepthLevel>('moderate');
  const [length, setLength] = useState<LengthLevel>('moderate');
  const [numSpeakers, setNumSpeakers] = useState(DEFAULT_PODCAST_SPEAKERS);
  const [tone, setTone] = useState('Default');
  const [title, setTitle] = useState('');
  const [focusPrompt, setFocusPrompt] = useState('');

  // Direct-read
  const [ttsText, setTtsText] = useState('');
  const [ttsVoice, setTtsVoice] = useState<TtsVoice>('female');
  const ttsAbort = useRef<AbortController | null>(null);

  // Generation UI
  const [generating, setGenerating] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Upgrade gate
  const [upgrade, setUpgrade] = useState<{ label: string; requiredTier: 'basic' | 'pro' } | null>(null);

  const options = useMemo<GenOptions>(() => {
    if (format === 'tts') return { format, ttsCharCount: ttsText.length };
    if (format === 'audiobook') return { format, lengthLevel: length, tone: tone !== 'Default' ? tone : undefined };
    return {
      format,
      depthLevel: depth,
      numSpeakers,
      tone: tone !== 'Default' ? tone : undefined,
    };
  }, [format, depth, length, numSpeakers, tone, ttsText]);

  const gate = useMemo(() => evaluateGate(options, tier), [options, tier]);

  // Weekly generation quota (rolling 7 days). Fail open if usage is unavailable.
  const quotaReached = Boolean(usage && usage.remaining <= 0);

  // Blocks when the weekly quota is spent; prompts free users to upgrade.
  const blockForQuota = (): boolean => {
    if (!quotaReached) return false;
    if (tier === 'free') {
      setUpgrade({ label: 'More weekly generations', requiredTier: 'basic' });
    } else {
      setError(
        usage?.resetsAt
          ? `You've used all ${usage.limit} generations this week. More unlock ${formatReset(usage.resetsAt)}.`
          : "You've reached your weekly generation limit.",
      );
    }
    return true;
  };

  const depthLocked = (d: DepthLevel) => tier === 'free' && !FREE_PODCAST_DEPTHS.includes(d);
  const speakersLocked = (n: number) => tier === 'free' && n > DEFAULT_PODCAST_SPEAKERS;
  const toneLocked = tier === 'free';
  const audiobookLocked = TIER_RANK[tier] < TIER_RANK.pro;

  const openUpgrade = () => {
    const required = gate.requiredTier === 'pro' ? 'pro' : 'basic';
    const label = gate.locks[0]?.label ?? 'This feature';
    setUpgrade({ label, requiredTier: required });
  };

  const resetResult = () => {
    setError(null);
    setDone(null);
    setStage(null);
  };

  const runDocGeneration = async () => {
    resetResult();
    if (!gate.allowed) {
      openUpgrade();
      return;
    }
    if (selectedDocs.length === 0) {
      setError('Select at least one document.');
      return;
    }
    if (blockForQuota()) return;
    setGenerating(true);
    setStage('Starting generation');
    try {
      const payload: GenerateAudioPayload = {
        document_ids: selectedDocs,
        format: format === 'audiobook' ? 'audiobook' : 'podcast',
        title: title.trim() || undefined,
        custom_search_prompt: focusPrompt.trim() || undefined,
        // Inert until AI-Service wires them (BE3), sent for forward-compat.
        tone: tone !== 'Default' ? tone.toLowerCase() : undefined,
      };
      if (format === 'podcast') {
        payload.depth_level = depth;
        payload.num_speakers = numSpeakers;
      } else {
        payload.length_level = length;
      }

      const result = await generateAudioStream(payload, (s) => setStage(s));
      // Usage is incremented server-side on generation; refresh to reflect it.
      refreshUsage();
      window.dispatchEvent(new CustomEvent(AUDIO_CREATED_EVENT, { detail: { format: payload.format } }));

      if (result.document_id) {
        await player.play({
          id: result.audio_content_id ?? result.document_id,
          title: result.title || title.trim() || 'Generated audio',
          documentId: result.document_id,
          format: payload.format,
          durationSeconds: result.duration_seconds,
        });
      }
      setDone(`Your ${payload.format} is ready and now playing.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed.');
    } finally {
      setGenerating(false);
      setStage(null);
    }
  };

  const runTts = async () => {
    resetResult();
    if (!gate.allowed) {
      openUpgrade();
      return;
    }
    const text = ttsText.trim();
    if (!text) {
      setError('Paste some text to read aloud.');
      return;
    }
    if (text.length > MAX_TTS_CHARS) {
      setError(`Text is too long. Max ${MAX_TTS_CHARS.toLocaleString()} characters.`);
      return;
    }
    if (blockForQuota()) return;
    setGenerating(true);
    setStage('Synthesizing audio');
    ttsAbort.current = new AbortController();
    try {
      const blob = await textToSpeech({ text, voice: ttsVoice, signal: ttsAbort.current.signal });
      // Usage is incremented server-side on generation; refresh to reflect it.
      refreshUsage();
      const url = URL.createObjectURL(blob);
      await player.play({ id: `tts-${Date.now()}`, title: 'Read-aloud', format: 'tts', directUrl: url });
      setDone('Playing your read-aloud audio.');
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') {
        setError(e instanceof Error ? e.message : 'Could not generate audio.');
      }
    } finally {
      setGenerating(false);
      setStage(null);
      ttsAbort.current = null;
    }
  };

  const docLabel =
    selectedDocs.length === 0
      ? 'Select documents'
      : `${selectedDocs.length} document${selectedDocs.length > 1 ? 's' : ''} selected`;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Create</h1>
          <p className="page__subtitle">Generate a podcast, audiobook, or read-aloud from your documents.</p>
        </div>
      </div>

      {/* Format tabs */}
      <div className="fmt-tabs">
        <button className={`fmt-tab${format === 'podcast' ? ' fmt-tab--active' : ''}`} onClick={() => setFormat('podcast')}>
          <FiMic /> Podcast <span className="fmt-tab__free">Free</span>
        </button>
        <button className={`fmt-tab${format === 'audiobook' ? ' fmt-tab--active' : ''}`} onClick={() => setFormat('audiobook')}>
          <FiBookOpen /> Audiobook {audiobookLocked && <FiLock className="fmt-tab__lock" />}
        </button>
        <button className={`fmt-tab${format === 'tts' ? ' fmt-tab--active' : ''}`} onClick={() => setFormat('tts')}>
          <FiVolume2 /> Direct Read
        </button>
      </div>

      <div className="create-grid">
        <div className="create-main card">
          {format === 'tts' ? (
            <TtsForm
              text={ttsText}
              setText={setTtsText}
              voice={ttsVoice}
              setVoice={setTtsVoice}
              tier={tier}
            />
          ) : (
            <>
              <button className="doc-select" onClick={() => setPickerOpen(true)}>
                <span className="doc-select__icon">
                  <FiFileText />
                </span>
                <span className="doc-select__text">
                  <strong>{docLabel}</strong>
                  <span className="muted">Choose files to generate from</span>
                </span>
              </button>

              {/* Length / complexity */}
              <div className="field create-field">
                <label className="field__label">{format === 'podcast' ? 'Length & depth' : 'Length'}</label>
                <div className="seg">
                  {(format === 'podcast' ? PODCAST_DEPTHS : AUDIOBOOK_LENGTHS).map((opt) => {
                    const locked =
                      format === 'podcast'
                        ? depthLocked(opt.value as DepthLevel)
                        : audiobookLocked;
                    const active = format === 'podcast' ? depth === opt.value : length === opt.value;
                    return (
                      <button
                        key={opt.value}
                        className={`seg__btn${active ? ' seg__btn--active' : ''}`}
                        onClick={() =>
                          format === 'podcast'
                            ? setDepth(opt.value as DepthLevel)
                            : setLength(opt.value as LengthLevel)
                        }
                      >
                        <span className="seg__label">
                          {opt.label} {locked && <FiLock className="seg__lock" />}
                        </span>
                        <span className="seg__hint">{opt.hint}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Podcast-only: number of voices */}
              {format === 'podcast' && (
                <div className="field create-field">
                  <label className="field__label">
                    Voices <span className="muted">(hosts)</span>
                  </label>
                  <div className="seg seg--compact">
                    {SPEAKER_OPTIONS.map((n) => (
                      <button
                        key={n}
                        className={`seg__btn${numSpeakers === n ? ' seg__btn--active' : ''}`}
                        onClick={() => setNumSpeakers(n)}
                      >
                        <span className="seg__label">
                          {n} {speakersLocked(n) && <FiLock className="seg__lock" />}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Tone */}
              <div className="field create-field">
                <label className="field__label" htmlFor="tone">
                  Tone {toneLocked && <FiLock className="inline-lock" />}
                </label>
                <select id="tone" className="select" value={tone} onChange={(e) => setTone(e.target.value)}>
                  {TONES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                      {t !== 'Default' ? ' (premium)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div className="field create-field">
                <label className="field__label" htmlFor="title">
                  Title <span className="muted">(optional)</span>
                </label>
                <input
                  id="title"
                  className="input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Chapter 5 Deep Dive"
                />
              </div>

              {/* Focus prompt */}
              <div className="field create-field">
                <label className="field__label" htmlFor="focus">
                  Focus prompt <span className="muted">(optional)</span>
                </label>
                <textarea
                  id="focus"
                  className="textarea"
                  value={focusPrompt}
                  onChange={(e) => setFocusPrompt(e.target.value)}
                  placeholder="e.g. Focus on the key theories and give real-world examples"
                />
              </div>
            </>
          )}

          {error && <div className="create-error">{error}</div>}
          {done && (
            <div className="create-done">
              {done} <button className="link-btn" onClick={() => navigate('/library')}>View library</button>
            </div>
          )}

          <button
            className="btn btn--primary btn--block btn--lg create-generate"
            onClick={format === 'tts' ? runTts : runDocGeneration}
            disabled={generating}
          >
            {generating ? (
              <>
                <span className="spinner spinner--sm" /> {stage || 'Generating…'}
              </>
            ) : !gate.allowed ? (
              <>
                <FiLock /> Unlock &amp; generate
              </>
            ) : (
              <>
                {format === 'audiobook' ? <FiBookOpen /> : format === 'tts' ? <FiVolume2 /> : <FiMic />} Generate{' '}
                {format === 'audiobook' ? 'audiobook' : format === 'tts' ? '& play' : 'podcast'}
              </>
            )}
          </button>

          {usage && (
            <p className="create-note muted">
              {usage.remaining > 0
                ? `${usage.remaining} of ${usage.limit} generation${usage.limit === 1 ? '' : 's'} left this week.`
                : `No generations left this week${usage.resetsAt ? `. Resets ${formatReset(usage.resetsAt)}` : ''}.`}
            </p>
          )}

          {tier === 'free' && (
            <p className="create-note muted">
              Basic podcasts are free (ad-supported). Longer runs, extra voices, tone, audiobooks and long
              read-alouds need a plan or a one-off unlock.
            </p>
          )}
        </div>

        <div className="create-aside">
          <AdSlot format="rectangle" label="Sponsored" />
        </div>
      </div>

      {generating && (
        <div className="gen-overlay">
          <div className="gen-overlay__card">
            <span className="spinner" />
            <p className="gen-overlay__stage">{stage || 'Working…'}</p>
            <p className="muted">This can take 1–3 minutes. Keep this tab open.</p>
            {format === 'tts' && (
              <button
                className="btn btn--ghost"
                onClick={() => {
                  ttsAbort.current?.abort();
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      <DocumentPickerModal
        open={pickerOpen}
        documents={documents}
        selectedIds={selectedDocs}
        isReady={isReady}
        onClose={() => setPickerOpen(false)}
        onConfirm={(ids) => {
          setSelectedDocs(ids);
          setPickerOpen(false);
        }}
      />

      <UpgradeModal
        open={Boolean(upgrade)}
        onClose={() => setUpgrade(null)}
        requiredTier={upgrade?.requiredTier ?? null}
        featureLabel={upgrade?.label ?? 'This feature'}
        onSubscribe={() => {
          setUpgrade(null);
          navigate('/account');
        }}
      />
    </div>
  );
}

function TtsForm({
  text,
  setText,
  voice,
  setVoice,
  tier,
}: {
  text: string;
  setText: (v: string) => void;
  voice: TtsVoice;
  setVoice: (v: TtsVoice) => void;
  tier: string;
}) {
  const over = text.length > MAX_TTS_CHARS;
  const overFree = tier === 'free' && text.length > FREE_TTS_CHAR_CAP;
  return (
    <>
      <div className="field create-field">
        <label className="field__label" htmlFor="tts">
          Text to read aloud
        </label>
        <textarea
          id="tts"
          className="textarea"
          style={{ minHeight: 220 }}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste or type the text you want read aloud…"
        />
        <div className={`tts-count${over || overFree ? ' tts-count--warn' : ''}`}>
          {text.length.toLocaleString()} / {MAX_TTS_CHARS.toLocaleString()} characters
          {overFree && !over && (
            <span className="inline-lock">
              {' '}
              <FiLock /> free up to {FREE_TTS_CHAR_CAP.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      <div className="field create-field">
        <label className="field__label">Voice</label>
        <div className="seg seg--compact">
          <button className={`seg__btn${voice === 'female' ? ' seg__btn--active' : ''}`} onClick={() => setVoice('female')}>
            <span className="seg__label">👩 Female</span>
          </button>
          <button className={`seg__btn${voice === 'male' ? ' seg__btn--active' : ''}`} onClick={() => setVoice('male')}>
            <span className="seg__label">👨 Male</span>
          </button>
        </div>
      </div>
    </>
  );
}
